import React from 'react';
import {
    MdArrowForward,
    MdArticle,
    MdOpenInNew,
    MdLanguage,
    MdOndemandVideo,
    MdHeadphones,
    MdAutoAwesome,
    MdChecklist,
    MdContentCopy,
    MdEvent,
    MdMic,
    MdMicOff,
    MdRestartAlt,
    MdSend,
    MdStop,
    MdVolumeUp,
} from 'react-icons/md';
import { RiRobot3Line } from 'react-icons/ri';
import { FaYoutube } from 'react-icons/fa';
import { LuFileText, LuGlobe as LuGlobe2 } from 'react-icons/lu';
import { toast } from 'react-toastify';
import { useAuth } from '../../../Context/AuthContext';
import { isSuperAdminUser } from '../../../Utils/Permissions';
import { getUserInitials } from '../../../Utils/UserInitials';
import { getAllClientNames } from '../../../Services/ClientService';
import { clearAegisAiChatSession, sendAegisAiChatMessage, streamAegisAiChatMessage } from '../../../Services/AegisAiChatService';
import { createSessionId } from '../../../Utils/SessionId';
import { getAllAegisAiDocuments } from '../../../Services/AegisAiDocumentsService';
import { endpoints } from '../../../Utils/Constants';
import SearchableDropdown from '../../../Components/Subscription/SearchableDropdown';
import PageTitle from '../../../Components/Common/PageTitle';
import ChatMessageRenderer, { getSpeakableSegments } from '../../../Components/Analytics/ChatMessageRenderer';
import '../../../Styles/AnalyticsChat.css';
import '../../../Styles/AegisAi.css';
import '../../../Styles/AegisAiChat.css';

const TEXTAREA_MAX_HEIGHT = 160;

const getEmmaVoice = () => {
    if (!('speechSynthesis' in window)) return null;
    const voices = window.speechSynthesis.getVoices();
    // 1. Exact match for Microsoft Emma Online (Natural)
    let emma = voices.find((v) => v.name.includes('Emma Online (Natural)'));
    // 2. Any voice named Emma
    if (!emma) emma = voices.find((v) => v.name.toLowerCase().includes('emma'));
    // 3. Fallback to another Microsoft Online Natural US English voice
    if (!emma) emma = voices.find((v) => v.name.includes('Online (Natural)') && (v.lang === 'en-US' || v.lang.startsWith('en')));
    // 4. Fallback to any en-US voice
    if (!emma) emma = voices.find((v) => v.lang === 'en-US' || v.lang.startsWith('en'));
    return emma || voices[0] || null;
};
const STARTER_PROMPTS = [
    { icon: MdArticle, text: 'Summarize the key points in these documents' },
    { icon: MdEvent, text: 'What important dates or deadlines are mentioned?' },
    { icon: MdChecklist, text: 'List any action items or requirements' },
];

const nextMessageId = () => `msg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const formatTime = (date) => date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const getCurrentClientTime = () => {
    const now = new Date();
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return `${now.toLocaleString(undefined, { dateStyle: 'full', timeStyle: 'short' })}${tz ? ` (${tz})` : ''}`;
};


const cleanCitationContent = (text) => {
    if (!text || typeof text !== 'string') return '';
    return text
        .replace(/\[(?:Image|Figure|Table|Diagram|Photo)\s+from\s+[^\n\]]+\]/gi, '')
        .replace(/\[\d+\]\s*\[Source:.*?\]/gi, '')
        .replace(/\[Source:.*?\]/gi, '')
        .replace(/\[Document:\s*[^\n\]]+\]/gi, '')
        .replace(/Image (?:URL|ID):\s*\S+/gi, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
};

const SourcesCollapsible = ({ sources }) => {
    const [isOpen, setIsOpen] = React.useState(false);

    if (!sources || !Array.isArray(sources) || sources.length === 0) return null;

    // Deduplicate by title / document_name and merge page numbers and previews
    const uniqueMap = new Map();
    for (const s of sources) {
        if (!s) continue;
        const title = (s.document_name || s.title || 'Document').trim();
        const key = title.toLowerCase();

        // Collect page numbers
        const pageSet = new Set();
        if (Array.isArray(s.page_numbers)) {
            for (const n of s.page_numbers) {
                const parsed = parseInt(n, 10);
                if (!isNaN(parsed) && parsed > 0) pageSet.add(parsed);
            }
        }
        if (s.page_no) {
            const rawStr = String(s.page_no);
            for (const p of rawStr.split(',')) {
                const parsed = parseInt(p.trim(), 10);
                if (!isNaN(parsed) && parsed > 0) pageSet.add(parsed);
            }
        }

        const isTextBased =
            s.is_text_based ||
            s.source_type === 'youtube' ||
            s.source_type === 'audio' ||
            s.source_type === 'video' ||
            s.source_type === 'website' ||
            s.source_type === 'custom_text' ||
            (s.source_url && (s.source_url.includes('youtube.com') || s.source_url.includes('youtu.be')));

        const pagePreviews = { ...(s.page_previews || {}) };

        if (!uniqueMap.has(key)) {
            uniqueMap.set(key, {
                title,
                source_type: s.source_type,
                source_url: s.source_url,
                is_text_based: isTextBased,
                pages: pageSet,
                page_previews: pagePreviews,
            });
        } else {
            const existing = uniqueMap.get(key);
            for (const p of pageSet) {
                existing.pages.add(p);
            }
            if (s.page_previews) {
                for (const [pKey, pVal] of Object.entries(s.page_previews)) {
                    if (!existing.page_previews[pKey] && pVal) {
                        existing.page_previews[pKey] = pVal;
                    }
                }
            }
            if (!existing.source_url && s.source_url) {
                existing.source_url = s.source_url;
            }
            if (!existing.source_type && s.source_type) {
                existing.source_type = s.source_type;
            }
        }
    }

    const cleanSources = Array.from(uniqueMap.values()).map((item) => {
        const sortedPages = Array.from(item.pages).sort((a, b) => a - b);
        return {
            ...item,
            sortedPages,
        };
    });

    if (cleanSources.length === 0) return null;

    return (
        <div className="hc-aic-citations-container">
            <button
                type="button"
                className="hc-aic-citations-toggle"
                onClick={() => setIsOpen((prev) => !prev)}
                aria-expanded={isOpen}
                aria-label={isOpen ? 'Collapse sources' : 'Expand sources'}
            >
                <span className="hc-aic-citations-toggle-text">Sources</span>
                <span className="hc-aic-citations-toggle-arrow">
                    {isOpen ? '▲' : '▼'}
                </span>
            </button>

            {isOpen && (
                <div className="hc-aic-citations-dropdown">
                    {cleanSources.map((src, sIdx) => {
                        const isYoutube =
                            src.source_type === 'youtube' ||
                            (src.source_url &&
                                (src.source_url.includes('youtube.com') || src.source_url.includes('youtu.be')));
                        const isAudio = src.source_type === 'audio' || src.source_type === 'video';
                        const isWeb = src.source_type === 'website';
                        const isLink = Boolean(src.source_url);

                        let IconComponent = MdArticle;
                        let iconColor = '#014c54';
                        if (isYoutube) {
                            IconComponent = FaYoutube;
                            iconColor = '#e52d27';
                        } else if (isAudio) {
                            IconComponent = MdHeadphones;
                            iconColor = '#7c3aed';
                        } else if (isWeb) {
                            IconComponent = LuGlobe2;
                            iconColor = '#3c9fa2';
                        } else if (src.is_text_based) {
                            IconComponent = LuFileText;
                            iconColor = '#0d9488';
                        }

                        const hasPages = !src.is_text_based && !isYoutube && !isAudio && !isWeb && src.sortedPages.length > 0;

                        if (hasPages) {
                            return src.sortedPages.map((pageNum, pIdx) => {
                                const preview = src.page_previews?.[String(pageNum)];
                                return (
                                    <div key={`${sIdx}-${pIdx}`} className="hc-aic-citation-card">
                                        <div className="hc-aic-citation-card-header">
                                            <div className="hc-aic-citation-title-wrap">
                                                <IconComponent
                                                    className="hc-aic-citation-icon flex-shrink-0"
                                                    size={16}
                                                    style={{ color: iconColor }}
                                                />
                                                {isLink ? (
                                                    <a
                                                        href={src.source_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="hc-aic-citation-name is-link"
                                                        title={`Open ${src.title}`}
                                                    >
                                                        <span className="text-truncate">{src.title}</span>
                                                        <MdOpenInNew size={12} className="hc-aic-citation-link-icon flex-shrink-0" />
                                                    </a>
                                                ) : (
                                                    <span className="hc-aic-citation-name text-truncate">
                                                        {src.title}
                                                    </span>
                                                )}
                                            </div>
                                            <span className="hc-aic-citation-page-badge">
                                                Page {pageNum}
                                            </span>
                                        </div>
                                        {preview && (
                                            <div className="hc-aic-citation-page-preview">
                                                {cleanCitationContent(preview)}
                                            </div>
                                        )}
                                    </div>
                                );
                            });
                        }

                        const textPreview =
                            src.page_previews?.['0'] ||
                            src.page_previews?.['text'] ||
                            Object.values(src.page_previews || {})[0];

                        return (
                            <div key={sIdx} className="hc-aic-citation-card">
                                <div className="hc-aic-citation-card-header">
                                    <div className="hc-aic-citation-title-wrap">
                                        <IconComponent
                                            className="hc-aic-citation-icon flex-shrink-0"
                                            size={16}
                                            style={{ color: iconColor }}
                                        />
                                        {isLink ? (
                                            <a
                                                href={src.source_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="hc-aic-citation-name is-link"
                                                title={`Open ${src.title}`}
                                            >
                                                <span className="text-truncate">{src.title}</span>
                                                <MdOpenInNew size={12} className="hc-aic-citation-link-icon flex-shrink-0" />
                                            </a>
                                        ) : (
                                            <span className="hc-aic-citation-name text-truncate">
                                                {src.title}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {textPreview && (
                                    <div className="hc-aic-citation-page-preview">
                                        {cleanCitationContent(textPreview)}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};


// Inside Teams, answer images are served by the Teams endpoint, which accepts the Teams sign-on token.
const RAG_IMAGE_PATH = '/api/aegisai/rag/image/';
const TEAMS_IMAGE_PATH = '/api/aegisai/teams/image/';

// `teams` ({ token, getToken, userName, theme }) runs the page as the Microsoft Teams tab: signed in with the
// Teams sign-on token, always on the organisation's one client, and styled to the Teams theme.
export default function AegisAiChat({ teams = null }) {
    const { token, decodedToken } = useAuth();
    const isSuperAdmin = !teams && isSuperAdminUser(decodedToken);
    const accountId = decodedToken?.account_id;
    const userName = teams ? teams.userName : decodedToken?.username;
    // The Teams app is branded Astes AI
    const assistantName = teams ? 'Astes AI' : 'Aegis AI';
    const userInitials = getUserInitials(userName || 'User');

    const [clients, setClients] = React.useState([]);
    const [clientsLoading, setClientsLoading] = React.useState(false);
    const [selectedClientId, setSelectedClientId] = React.useState(isSuperAdmin ? null : accountId);
    const [chatsByClient, setChatsByClient] = React.useState({});
    const [messages, setMessages] = React.useState([]);
    const [inputValue, setInputValue] = React.useState('');
    const [speakingMessageId, setSpeakingMessageId] = React.useState(null);
    const [speakingWordIndex, setSpeakingWordIndex] = React.useState(-1);
    const [streamingMessageId, setStreamingMessageId] = React.useState(null);
    const [sending, setSending] = React.useState(false);
    const [thinkingText, setThinkingText] = React.useState('Thinking…');
    const messagesEndRef = React.useRef(null);
    const inputRef = React.useRef(null);
    const utteranceRef = React.useRef(null);
    const cadenceTimerRef = React.useRef(null);
    const [isListening, setIsListening] = React.useState(false);
    const recognitionRef = React.useRef(null);
    const basePromptRef = React.useRef('');
    // Names this conversation so the assistant understands follow-up questions; replaced on "New chat".
    const sessionIdRef = React.useRef(createSessionId());

    // Another client's knowledge base must not inherit this conversation's memory.
    React.useEffect(() => {
        sessionIdRef.current = createSessionId();
    }, [selectedClientId]);

    React.useEffect(() => {
        if (!isSuperAdmin || !token) return;
        (async () => {
            try {
                setClientsLoading(true);
                const [response, documentsResponse] = await Promise.all([
                    getAllClientNames(token),
                    getAllAegisAiDocuments(token),
                ]);
                if (response?.is_success) {
                    const allClients = Array.isArray(response?.data) ? response.data : [];
                    // Only clients with at least one uploaded document can be chatted with.
                    const documents = documentsResponse?.is_success && Array.isArray(documentsResponse?.data?.Documents)
                        ? documentsResponse.data.Documents
                        : [];
                    const clientIdsWithDocs = new Set(
                        documents.map((doc) => doc?.ClientId).filter(Boolean).map(String)
                    );
                    const clientNamesWithDocs = new Set(
                        documents.map((doc) => (doc?.ClientName || '').trim().toLowerCase()).filter(Boolean)
                    );
                    setClients(allClients.filter((client) =>
                        clientIdsWithDocs.has(String(client.Id)) ||
                        clientNamesWithDocs.has((client.OrganizationName || '').trim().toLowerCase())
                    ));
                } else {
                    setClients([]);
                }
            } catch (error) {
                console.error(error);
                setClients([]);
            } finally {
                setClientsLoading(false);
            }
        })();
    }, [isSuperAdmin, token]);

    React.useEffect(() => {
        if ('speechSynthesis' in window) {
            window.speechSynthesis.getVoices();
            const handleVoicesChanged = () => window.speechSynthesis.getVoices();
            window.speechSynthesis.onvoiceschanged = handleVoicesChanged;
        }
        return () => {
            if ('speechSynthesis' in window) {
                window.speechSynthesis.cancel();
            }
            if (cadenceTimerRef.current) {
                clearInterval(cadenceTimerRef.current);
                cadenceTimerRef.current = null;
            }
        };
    }, []);

    React.useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, sending]);

    React.useEffect(() => {
        if (!inputRef.current) return;
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, TEXTAREA_MAX_HEIGHT)}px`;
    }, [inputValue]);

    React.useEffect(() => {
        return () => {
            if (recognitionRef.current) {
                try {
                    recognitionRef.current.stop();
                } catch (e) {}
            }
        };
    }, []);

    const stopVoiceRecognition = () => {
        if (recognitionRef.current) {
            try {
                // Detach handlers immediately so late recognition events never re-populate input
                recognitionRef.current.onresult = null;
                recognitionRef.current.onerror = null;
                recognitionRef.current.onend = null;
                recognitionRef.current.abort();
            } catch (e) {}
            recognitionRef.current = null;
        }
        basePromptRef.current = '';
        setIsListening(false);
    };

    const handleToggleListen = () => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            toast.error('Voice recognition is not supported in this browser. Please use Google Chrome or Microsoft Edge.');
            return;
        }

        if (isListening) {
            stopVoiceRecognition();
            return;
        }

        // Cancel assistant reading if it is currently speaking to avoid microphone echo
        if (window.speechSynthesis && window.speechSynthesis.speaking) {
            window.speechSynthesis.cancel();
            setSpeakingMessageId(null);
            setSpeakingWordIndex(-1);
        }

        try {
            const recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = 'en-US';

            basePromptRef.current = inputValue ? inputValue.trim() + ' ' : '';

            recognition.onstart = () => {
                setIsListening(true);
            };

            recognition.onresult = (event) => {
                let sessionTranscript = '';
                for (let i = 0; i < event.results.length; i++) {
                    sessionTranscript += event.results[i][0].transcript;
                }
                const updated = (basePromptRef.current + sessionTranscript).replace(/\s+/g, ' ');
                setInputValue(updated);
            };

            recognition.onerror = (event) => {
                console.warn('[Aegis Voice Recognition] Error:', event.error);
                if (event.error === 'not-allowed' || event.error === 'permission-denied') {
                    toast.error('Microphone access denied. Please allow microphone permissions in your browser.');
                } else if (event.error !== 'no-speech' && event.error !== 'aborted') {
                    toast.error(`Voice recognition error: ${event.error}`);
                }
                setIsListening(false);
                recognitionRef.current = null;
            };

            recognition.onend = () => {
                setIsListening(false);
                recognitionRef.current = null;
            };

            recognitionRef.current = recognition;
            recognition.start();
        } catch (err) {
            console.error('[Aegis Voice Recognition] Failed to start:', err);
            toast.error('Could not activate microphone.');
            setIsListening(false);
            recognitionRef.current = null;
        }
    };

    const sendPrompt = async (overrideText) => {
        stopVoiceRecognition();
        const text = (overrideText ?? inputValue).trim();
        if (!text || sending) return;

        if (isSuperAdmin && !selectedClientId) {
            toast.error('Select a client before starting the chat.');
            return;
        }

        // Immediately empty the input box and reset textarea height
        setInputValue('');
        basePromptRef.current = '';
        if (inputRef.current) {
            inputRef.current.value = '';
            inputRef.current.style.height = 'auto';
        }

        const userMessage = { id: nextMessageId(), role: 'user', text, time: new Date() };
        setMessages((prev) => [...prev, userMessage]);
        setThinkingText('Thinking…');
        setSending(true);

        const assistantMsgId = nextMessageId();
        let hasAppendedAssistant = false;
        let messageImages = [];
        let messageSources = [];

        try {
            const payload = {
                query: text,
                client_id: isSuperAdmin ? selectedClientId : undefined,
                session_id: sessionIdRef.current,
                ...(teams && {
                    is_teams: true,
                    client_time: getCurrentClientTime(),
                }),
            };
            const authToken = teams ? await teams.getToken() : token;

            await streamAegisAiChatMessage(authToken, payload, {
                onThinking: (statusMsg) => {
                    if (statusMsg) {
                        setThinkingText(statusMsg);
                    }
                },
                onSources: (sources) => {
                    if (Array.isArray(sources) && sources.length > 0) {
                        messageSources = sources;
                        setMessages((prev) =>
                            prev.map((msg) =>
                                msg.id === assistantMsgId ? { ...msg, sources } : msg
                            )
                        );
                    }
                },
                onImages: (imgs) => {
                    if (Array.isArray(imgs) && imgs.length > 0) {
                        messageImages = imgs;
                        setMessages((prev) =>
                            prev.map((msg) =>
                                msg.id === assistantMsgId ? { ...msg, images: imgs } : msg
                            )
                        );
                    }
                },
                onToken: (tokenChunk) => {
                    if (!hasAppendedAssistant) {
                        hasAppendedAssistant = true;
                        setSending(false);
                        setStreamingMessageId(assistantMsgId);
                        setMessages((prev) => [
                            ...prev,
                            { id: assistantMsgId, role: 'assistant', text: tokenChunk, images: messageImages, sources: messageSources, time: new Date() }
                        ]);
                    } else {
                        setMessages((prev) =>
                            prev.map((msg) =>
                                msg.id === assistantMsgId ? { ...msg, text: msg.text + tokenChunk } : msg
                            )
                        );
                    }
                },
                onDone: () => {
                    setSending(false);
                    setStreamingMessageId(null);
                },
                onError: (errMessage) => {
                    setSending(false);
                    setStreamingMessageId(null);
                    toast.error(errMessage || 'Something went wrong while sending your message.');
                }
            }, undefined, teams ? endpoints.AEGIS_AI_TEAMS_CHAT_STREAM : undefined);
        } catch (error) {
            console.error(error);
            toast.error('Something went wrong while sending your message.');
            setSending(false);
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        sendPrompt();
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            sendPrompt();
        }
    };

    const handleClientChange = (newClientId) => {
        const nextId = newClientId || null;
        if (nextId === selectedClientId) return;
        if (cadenceTimerRef.current) {
            clearInterval(cadenceTimerRef.current);
            cadenceTimerRef.current = null;
        }
        if ('speechSynthesis' in window) {
            window.speechSynthesis.cancel();
        }
        setSpeakingMessageId(null);
        setSpeakingWordIndex(-1);

        // Save current messages for client we are leaving
        if (selectedClientId) {
            setChatsByClient((prev) => ({
                ...prev,
                [selectedClientId]: messages,
            }));
        }

        setSelectedClientId(nextId);

        // Switch to the newly selected client's chat history or show brand new chat
        const nextMessages = nextId && chatsByClient[nextId] ? chatsByClient[nextId] : [];
        setMessages(nextMessages);
        setInputValue('');
        setSending(false);
    };

    const handleNewChat = () => {
        if (sending) return;
        if (cadenceTimerRef.current) {
            clearInterval(cadenceTimerRef.current);
            cadenceTimerRef.current = null;
        }
        if ('speechSynthesis' in window) {
            window.speechSynthesis.cancel();
        }
        setSpeakingMessageId(null);
        setSpeakingWordIndex(-1);
        if (messages.length) {
            if (teams) clearAegisAiChatSession(teams.token, sessionIdRef.current, endpoints.AEGIS_AI_TEAMS_CLEAR_SESSION);
            else clearAegisAiChatSession(token, sessionIdRef.current);
        }
        sessionIdRef.current = createSessionId();
        setMessages([]);
        if (selectedClientId) {
            setChatsByClient((prev) => {
                const updated = { ...prev };
                delete updated[selectedClientId];
                return updated;
            });
        }
    };

    const handleCopy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success('Copied to clipboard');
        } catch (error) {
            console.error(error);
            toast.error('Could not copy to clipboard');
        }
    };

    const handleToggleSpeak = (messageKey, rawText) => {
        if (!('speechSynthesis' in window)) {
            toast.error('Voice reading is not supported in this browser.');
            return;
        }

        // Clear any ongoing cadence timer
        if (cadenceTimerRef.current) {
            clearInterval(cadenceTimerRef.current);
            cadenceTimerRef.current = null;
        }

        // If currently speaking this exact response, toggle it off
        if (speakingMessageId === messageKey) {
            window.speechSynthesis.cancel();
            utteranceRef.current = null;
            setSpeakingMessageId(null);
            setSpeakingWordIndex(-1);
            return;
        }

        // Stop any previously playing audio immediately
        window.speechSynthesis.cancel();
        utteranceRef.current = null;
        setSpeakingWordIndex(-1);

        // Speak exactly the words the renderer displays (same tokenisation), so each spoken word maps
        // 1:1 to a highlighted word. `index` is the display word index; `start`/`end` locate the
        // spoken form in cleanText for boundary events.
        const wordMap = [];
        let cleanText = '';
        let cumulativeMs = 0;
        let displayIndex = 0;
        const baseMsPerWord = 390; // ~155 WPM for natural speech

        getSpeakableSegments(rawText).forEach((words) => {
            words.forEach((word, wordPos) => {
                const index = displayIndex++;
                // Drop markdown leftovers (link targets, code ticks, emphasis markers) from the spoken form
                let spoken = word.replace(/\]\([^)]*\)?/g, '').replace(/[`*_#|[\]>]/g, '');
                if (!/[\p{L}\p{N}]/u.test(spoken)) return;
                // Pause between headings, list items, paragraphs and table cells
                if (wordPos === words.length - 1 && !/[.!?:;,]$/.test(spoken)) spoken += '.';

                let dur = baseMsPerWord * (0.8 + 0.05 * Math.min(spoken.length, 12));
                if (spoken.endsWith(',')) dur += 180;
                else if (/[.!?:;]$/.test(spoken)) dur += 320;

                if (cleanText) cleanText += ' ';
                wordMap.push({
                    index,
                    start: cleanText.length,
                    end: cleanText.length + spoken.length,
                    startMs: cumulativeMs,
                    endMs: cumulativeMs + dur,
                });
                cleanText += spoken;
                cumulativeMs += dur;
            });
        });

        if (!wordMap.length) return;

        const utterance = new SpeechSynthesisUtterance(cleanText);
        const emmaVoice = getEmmaVoice();
        if (emmaVoice) {
            utterance.voice = emmaVoice;
        }
        utterance.rate = 1.0;
        utterance.pitch = 1.0;

        const cleanup = () => {
            if (cadenceTimerRef.current) {
                clearInterval(cadenceTimerRef.current);
                cadenceTimerRef.current = null;
            }
            utteranceRef.current = null;
            setSpeakingMessageId(null);
            setSpeakingWordIndex(-1);
        };

        const startTime = Date.now();
        // Estimated cadence is anchored to when audio actually starts (onstart), not to speak()
        let speechStartTime = null;
        // Once the voice emits real word boundaries, they are the only source of truth
        let boundarySupported = false;
        let lastBoundaryTime = 0;
        let currentIndex = wordMap[0].index;

        utterance.onstart = () => {
            speechStartTime = Date.now();
        };

        // Fallback cadence timer for voices that never emit word boundary events
        cadenceTimerRef.current = setInterval(() => {
            if (boundarySupported) {
                // Safety net only: onend normally clears the highlight. Online voices report
                // speechSynthesis.speaking unreliably, so rely on boundary silence instead.
                if (Date.now() - lastBoundaryTime > 8000) {
                    cleanup();
                }
                return;
            }
            if (speechStartTime === null) {
                // Some voices never fire onstart; start the estimate after a short delay
                if (Date.now() - startTime < 300) return;
                speechStartTime = Date.now();
            }
            const elapsed = Date.now() - speechStartTime;
            const activeEntry = wordMap.find((w) => elapsed >= w.startMs && elapsed < w.endMs);
            if (!activeEntry && elapsed >= cumulativeMs + 600) {
                cleanup();
                return;
            }
            // Never move the estimated highlight backwards
            if (activeEntry && activeEntry.index > currentIndex) {
                currentIndex = activeEntry.index;
                setSpeakingWordIndex(currentIndex);
            }
        }, 50);

        utterance.onboundary = (e) => {
            // Sentence boundaries report the sentence start and would pull the highlight back
            if (e.name && e.name !== 'word') return;
            if (e.charIndex === undefined) return;
            boundarySupported = true;
            lastBoundaryTime = Date.now();
            let found = wordMap.find((w) => e.charIndex >= w.start && e.charIndex < w.end);
            if (!found) {
                found = wordMap.find((w) => e.charIndex <= w.start);
            }
            if (found && found.index !== currentIndex) {
                currentIndex = found.index;
                setSpeakingWordIndex(currentIndex);
            }
        };

        utterance.onend = () => {
            const elapsed = Date.now() - startTime;
            // Guard against premature onend events in Chromium online voices
            if (elapsed > 1200 || wordMap.length <= 3) {
                cleanup();
            }
        };

        utterance.onerror = (e) => {
            // CRITICAL FIX: Do NOT cleanup on interrupted or canceled events!
            // Chrome/Edge fires interrupted/canceled asynchronously when cancelling previous audio,
            // which was falsely aborting the newly started speech highlight.
            if (e.error === 'interrupted' || e.error === 'canceled') {
                return;
            }
            console.error('Speech synthesis error:', e);
            cleanup();
        };

        utteranceRef.current = utterance;
        setSpeakingMessageId(messageKey);
        setSpeakingWordIndex(wordMap[0].index);
        window.speechSynthesis.speak(utterance);
    };

    const showEmptyState = messages.length === 0;

    return (
        <>
            <PageTitle title="Aegis AI Assist - Aegis 360" />

            <div className={`hc-ai-page hc-aic-page${teams ? ` is-teams is-teams-${teams.theme}` : ''}`}>
                <div className="hc-aic-shell d-flex flex-column overflow-hidden bg-white">
                    <div className="hc-aic-header flex-shrink-0 d-flex align-items-center justify-content-between text-white">
                        <span aria-hidden="true" className="hc-ai-orb rounded-circle hc-ai-orb-a" />
                        <div className="hc-aic-header-brand d-flex align-items-center">
                            <span className="hc-aic-header-avatar position-relative d-inline-flex align-items-center justify-content-center flex-shrink-0 text-white">
                                <RiRobot3Line size={20} />
                            </span>
                            <div className="hc-aic-header-title text-white text-truncate">{assistantName} Assistant</div>
                        </div>
                        <div className="hc-aic-header-actions d-flex align-items-center gap-2">
                            {isSuperAdmin ? (
                                <div className="hc-aic-header-client-picker mw-100">
                                    <SearchableDropdown
                                        placeholder="Select client"
                                        options={clients.map((client) => ({ value: client.Id, label: client.OrganizationName }))}
                                        value={selectedClientId ?? ''}
                                        onChange={handleClientChange}
                                        disabled={clientsLoading}
                                        loading={clientsLoading}
                                        showSearch
                                    />
                                </div>
                            ) : null}
                            {messages.length > 0 ? (
                                <button
                                    type="button"
                                    className="hc-aic-new-chat-btn align-items-center text-nowrap fw-bold text-white"
                                    onClick={handleNewChat}
                                    disabled={sending}
                                    aria-label="New chat"
                                    title="New chat"
                                >
                                    <MdRestartAlt size={16} />
                                    <span>New chat</span>
                                </button>
                            ) : null}
                        </div>
                    </div>

                    <div className="hc-aic-body position-relative flex-fill overflow-y-auto overflow-x-hidden">
                        {showEmptyState ? (
                            <div className="hc-aic-empty d-flex flex-column align-items-center text-center">
                                <div className="hc-aic-empty-orb position-relative d-flex align-items-center justify-content-center text-white">
                                    <span aria-hidden="true" className="hc-aic-empty-ring position-absolute" />
                                    <RiRobot3Line size={30} />
                                </div>
                                <h3 className="hc-aic-empty-title">Start a conversation</h3>
                                <p className="hc-aic-empty-text">
                                    {teams
                                        ? "Ask a question below. Astes AI answers only from your organization's knowledge base."
                                        : 'Ask a question below. Aegis AI will only ever refer to documents uploaded for this client.'}
                                </p>
                                <div className="hc-aic-starters w-100 d-grid">
                                    {STARTER_PROMPTS.map(({ icon: Icon, text }) => (
                                        <button
                                            key={text}
                                            type="button"
                                            className="hc-aic-starter-card position-relative d-flex text-start rounded-4"
                                            onClick={() => sendPrompt(text)}
                                        >
                                            <span className="hc-aic-starter-icon d-inline-flex align-items-center justify-content-center flex-shrink-0">
                                                <Icon size={18} />
                                            </span>
                                            <span className="hc-aic-starter-text fw-semibold">{text}</span>
                                            <MdArrowForward size={16} className="hc-aic-starter-arrow" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="hc-aic-thread d-flex flex-column">
                                {messages.map((message, index) => {
                                    const isUser = message.role === 'user';
                                    const messageKey = message.id ? `${message.id}-${index}` : `msg-${index}`;
                                    const isSpeakingThis = Boolean(speakingMessageId && speakingMessageId === messageKey);
                                    const isStreamingThis = Boolean(streamingMessageId && streamingMessageId === message.id);
                                    return (
                                        <div key={messageKey} className={`hc-aic-row d-flex w-100 ${isUser ? 'is-user' : 'is-assistant'}`}>
                                            {isUser ? (
                                                <span className="hc-aic-avatar-user d-inline-flex align-items-center justify-content-center flex-shrink-0 lh-1 rounded-circle hc-bg-text-primary text-white fw-semibold" title={userName || 'User'}>
                                                    {userInitials}
                                                </span>
                                            ) : (
                                                <span className="hc-aic-avatar d-inline-flex align-items-center justify-content-center flex-shrink-0 hc-aic-avatar-assistant text-white">
                                                    <RiRobot3Line size={17} />
                                                </span>
                                            )}
                                            <div className="hc-aic-msg d-flex flex-column">
                                                <div
                                                    className={`hc-aic-bubble mw-100 ${isUser ? 'hc-aic-bubble-user' : `hc-aic-bubble-assistant ${isSpeakingThis ? 'is-speaking' : ''}`}`}
                                                    style={isSpeakingThis ? { borderColor: '#0a6670', boxShadow: '0 4px 20px rgba(1, 76, 84, 0.16)' } : undefined}
                                                >
                                                    {isUser ? (
                                                        message.text
                                                    ) : (
                                                        <ChatMessageRenderer
                                                            content={teams ? message.text.replaceAll(RAG_IMAGE_PATH, TEAMS_IMAGE_PATH) : message.text}
                                                            isStreaming={isStreamingThis}
                                                            activeWordIndex={isSpeakingThis ? speakingWordIndex : -1}
                                                            imageToken={teams ? teams.token : undefined}
                                                        />
                                                    )}
                                                    {!isUser && message.sources && message.sources.length > 0 && !isStreamingThis && (
                                                        <SourcesCollapsible sources={message.sources} />
                                                    )}
                                                </div>
                                                <div className="hc-aic-msg-meta d-flex align-items-center py-0 px-1">
                                                    {isUser ? (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className="hc-aic-copy-btn d-inline-flex align-items-center gap-1 rounded-pill fw-semibold"
                                                                onClick={() => handleCopy(message.text)}
                                                                aria-label="Copy message"
                                                                title="Copy message"
                                                            >
                                                                <MdContentCopy size={13} />
                                                                <span>Copy</span>
                                                            </button>
                                                            <span>{formatTime(message.time)}</span>
                                                            <span aria-hidden="true">&middot;</span>
                                                            <span className="hc-aic-msg-author fw-bold">You</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span className="hc-aic-msg-author fw-bold">{assistantName}</span>
                                                            <span aria-hidden="true">&middot;</span>
                                                            <span>{formatTime(message.time)}</span>

                                                            {!isStreamingThis && (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        className="hc-aic-copy-btn d-inline-flex align-items-center gap-1 ms-1 rounded-pill fw-semibold"
                                                                        onClick={() => handleCopy(message.text)}
                                                                        aria-label="Copy response"
                                                                        title="Copy response"
                                                                    >
                                                                        <MdContentCopy size={13} />
                                                                        <span>Copy</span>
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className={`hc-aic-read-btn d-inline-flex align-items-center gap-1 ms-1 rounded-pill fw-semibold ${isSpeakingThis ? 'is-speaking' : ''}`}
                                                                        onClick={() => handleToggleSpeak(messageKey, teams ? message.text.replaceAll(RAG_IMAGE_PATH, TEAMS_IMAGE_PATH) : message.text)}
                                                                        aria-label={isSpeakingThis ? 'Stop' : 'Listen'}
                                                                        title={isSpeakingThis ? 'Stop' : 'Listen'}
                                                                    >
                                                                        {isSpeakingThis ? (
                                                                            <>
                                                                                <MdStop size={13} />
                                                                                <span>Stop</span>
                                                                                <span className="hc-aic-soundwave" aria-hidden="true">
                                                                                    <span />
                                                                                    <span />
                                                                                    <span />
                                                                                </span>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <MdVolumeUp size={13} />
                                                                                <span>Listen</span>
                                                                            </>
                                                                        )}
                                                                    </button>
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {sending ? (
                                    <div className="hc-aic-row d-flex w-100 is-assistant">
                                        <span className="hc-aic-avatar d-inline-flex align-items-center justify-content-center flex-shrink-0 hc-aic-avatar-assistant text-white is-thinking">
                                            <RiRobot3Line size={17} />
                                        </span>
                                        <div className="hc-aic-msg d-flex flex-column">
                                            <div className="hc-aic-bubble mw-100 hc-aic-bubble-assistant hc-aic-typing d-inline-flex align-items-center">
                                                <span className="hc-aic-typing-dots d-inline-flex align-items-center gap-1" aria-hidden="true">
                                                    <span />
                                                    <span />
                                                    <span />
                                                </span>
                                                <span className="hc-aic-typing-text fst-italic">
                                                    {thinkingText || `${assistantName} is thinking…`}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                                <div ref={messagesEndRef} />
                            </div>
                        )}
                    </div>

                    <form className="hc-aic-composer-wrap flex-shrink-0 bg-white" onSubmit={handleSubmit}>
                        <div className="hc-aic-composer d-flex align-items-end">
                            <MdAutoAwesome size={18} className="hc-aic-composer-spark flex-shrink-0 align-self-center" aria-hidden="true" />
                            <textarea
                                ref={inputRef}
                                className="hc-aic-textarea flex-fill border-0 overflow-y-auto"
                                placeholder={isListening ? "Listening... Speak now into your microphone" : teams ? "Ask a question about your organization's knowledge base..." : "Ask a question about this client's documents..."}
                                rows={1}
                                value={inputValue}
                                onChange={(event) => setInputValue(event.target.value)}
                                onKeyDown={handleKeyDown}
                                disabled={sending}
                                aria-label="Message"
                            />
                            <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                <button
                                    type="button"
                                    className={`hc-aic-mic-btn border-0 d-inline-flex align-items-center justify-content-center ${isListening ? 'is-listening' : ''}`}
                                    onClick={handleToggleListen}
                                    disabled={sending}
                                    aria-label={isListening ? 'Stop listening' : 'Voice input'}
                                    title={isListening ? 'Stop listening (Microphone active)' : 'Voice input (Speech to text)'}
                                >
                                    {isListening ? (
                                        <span className="hc-aic-mic-pulse-wrap">
                                            <MdMic size={20} className="hc-aic-mic-icon-active" />
                                            <span className="hc-aic-mic-ping" />
                                        </span>
                                    ) : (
                                        <MdMic size={20} />
                                    )}
                                </button>
                                <button
                                    type="submit"
                                    className="hc-aic-send-btn border-0 flex-shrink-0 d-inline-flex align-items-center justify-content-center text-white"
                                    disabled={sending || !inputValue.trim()}
                                    aria-label="Send message"
                                >
                                    {sending ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> : <MdSend size={18} />}
                                </button>
                            </div>
                        </div>
                        <div className="hc-aic-composer-hint d-flex align-items-center justify-content-between">
                            <span>Press Enter to send &middot; Shift + Enter for a new line</span>
                            {isListening && (
                                <span className="hc-aic-listening-indicator d-inline-flex align-items-center gap-1 text-danger fw-semibold">
                                    <span className="hc-aic-live-dot" />
                                    Listening... speak now
                                </span>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
}
